// ReviewQuestionSet.tsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  useToast,
  Input,
  VStack,
  HStack,
  Image,
  Divider,
  IconButton,
  Badge,
  Checkbox,
  Alert,
  AlertDescription,
  AlertTitle,
  CloseButton,
  Select,
  useColorModeValue,
} from "@chakra-ui/react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { QuestionSet, QuestionDetails, Question, QSetFull } from '../../Platform/Common/types';
import { RenderQuestionContext, RenderTextWithImages, QuestionContext } from "../../utils/questionRendering";
import B2CQuestionSetAuditLog from "../Components/B2CQuestionSetAuditLog";


type OperationMode = "none" | "resequence" | "delete" | "add" | "upload";

const B2CReviewQset = () => {
  // URL parameter
  const { question_set_id } = useParams<{ question_set_id: string }>();
  const toast = useToast();

  // UI color theme variables
  const cardbg = useColorModeValue("gray.50", "gray.700");

  // State variables
  const [questionSet, setQuestionSet] = useState<QSetFull | null>(null);
  const [questions, setQuestions] = useState<QuestionDetails[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadSummary, setUploadSummary] = useState<null | {
    added: number[];
    updated: number[];
    overridden: number[];      // NEW
    skipped_due_to_immutability: number[];
    errors: string[];
  }>(null);
  const [operationMode, setOperationMode] = useState<OperationMode>("none");
  const [selectedForDelete, setSelectedForDelete] = useState<number[]>([]);

  // For "Add Existing Question" mode:
  const [availableSequences, setAvailableSequences] = useState<number[]>([]);
  const [allQuestionSets, setAllQuestionSets] = useState<QuestionSet[]>([]);
  const [selectedQSetId, setSelectedQSetId] = useState<number | null>(null);
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [selectedSequence, setSelectedSequence] = useState<number | null>(null);

  // Add at top of component with other useColorModeValue calls:
  const contextPaneBg = useColorModeValue("blue.50", "gray.800");


  const currentQuestion = questions[currentIndex];

    const hasValidContext = (ctx: any): ctx is QuestionContext => {
    return Boolean(ctx) && typeof ctx === 'object' && 'type' in ctx;
  };

  // Fetch Question Set details
  const fetchQuestionSet = async () => {
    try {
      const apiUrl = process.env.REACT_APP_B2C_API_URL;
      const token = localStorage.getItem("b2c_token");
      const res = await axios.get(`${apiUrl}/qset-full/${question_set_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestionSet(res.data);
    } catch (error) {
      console.error("Failed to fetch question set", error);
    }
  };

  // Fetch Questions for this set
  const fetchQuestions = async () => {
    try {
      const apiUrl = process.env.REACT_APP_B2C_API_URL;
      const token = localStorage.getItem("b2c_token");
      const res = await axios.get(`${apiUrl}/qset/${question_set_id}/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuestions(res.data);
    } catch (error) {
      console.error("Failed to fetch questions", error);
    }
  };

  // Compute available sequence numbers based on current questions
  const computeAvailableSequences = () => {
    const usedSeqs = new Set(questions.map(q => q.sequence_number));
    const maxSeq = questions.length > 0 ? Math.max(...questions.map(q => q.sequence_number)) : 0;
    const possibleSeqs: number[] = [];
    for (let i = 1; i <= maxSeq + 1; i++) {
      if (!usedSeqs.has(i)) possibleSeqs.push(i);
    }
    setAvailableSequences(possibleSeqs);
  };

  // Fetch all question sets for "Add Existing" mode, excluding the current one
  const fetchAllQuestionSets = async () => {
     if (!questionSet?.question_set_id) return;
    try {
      const apiUrl = process.env.REACT_APP_B2C_API_URL;
      const token = localStorage.getItem("b2c_token");
      const res = await axios.get(`${apiUrl}/qsets`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const filtered = res.data.filter((qs: any) => qs.question_set_id !== questionSet.question_set_id);
      setAllQuestionSets(filtered);
    } catch (error) {
      console.error("Failed to fetch question sets", error);
    }
  };

  // Fetch questions from a selected question set for "Add Existing" mode
  const fetchQuestionsFromSet = async (qsetId: number) => {
    try {
      const apiUrl = process.env.REACT_APP_B2C_API_URL;
      const token = localStorage.getItem("b2c_token");
      const res = await axios.get(`${apiUrl}/qset/${qsetId}/questions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAvailableQuestions(res.data);
    } catch (error) {
      console.error("Failed to fetch questions from selected set", error);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0]);
      setUploadSummary(null);  // clear old message
    }
  };  

 const handleUpload = async () => {
  if (!file) {
    toast({
      title: "No file selected",
      description: "Please select a zip file first.",
      status: "warning",
      duration: 3000,
      isClosable: true,
    });
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    setIsUploading(true);

    const apiUrl = process.env.REACT_APP_B2C_API_URL || "http://localhost:8000";
    const response = await axios.post(
      `${apiUrl}/qset/bulk-upload-zip/${question_set_id}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("b2c_token")}`,
        },
      }
    );

    setUploadSummary(response.data);
    fetchQuestions();
  } catch (error) {
    setUploadSummary(null);

    toast({
      title: "Upload Failed",
      description: "An error occurred while uploading.",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  } finally {
    setIsUploading(false);
  }
};


  // Handlers for left pane question list navigation
  const handleQuestionClick = (index: number) => {
    setCurrentIndex(index);
  };
  const handleMoveUp = (index: number) => {
    if (index === 0 || operationMode !== "resequence") return;
    const updated = [...questions];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    setQuestions(updated);
  };
  const handleMoveDown = (index: number) => {
    if (index === questions.length - 1 || operationMode !== "resequence") return;
    const updated = [...questions];
    [updated[index + 1], updated[index]] = [updated[index], updated[index + 1]];
    setQuestions(updated);
  };

  // Commit handler: either resequence or delete, based on operationMode
  const handleCommit = async () => {
    const apiUrl = process.env.REACT_APP_B2C_API_URL;
    const token = localStorage.getItem("b2c_token");
    try {
      if (operationMode === "resequence") {
        // Resequence: compute new order from current questions list
        const updatedSequence = questions.map((q, i) => ({
          question_id: q.question_id,
          sequence_number: i + 1,
        }));
        await axios.put(`${apiUrl}/qset/${question_set_id}/reorder`, updatedSequence, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast({
          title: "New order committed",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else if (operationMode === "delete") {
        try {
          const { data } = await axios.delete(
            `${apiUrl}/qset/delete-questions/${question_set_id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              data: { question_ids: selectedForDelete },
            }
          );

          // ─── Unpack API result ──────────────────────────────────────────────
          const {
            deleted = [],
            unlinked_from_qset: unlinked = [],
            deactivated = [],
            errors = [],
          } = data || {};

          // ─── Build user-friendly message ────────────────────────────────────
          const parts: string[] = [];

          if (deleted.length)   parts.push(`${deleted.length} permanently deleted`);
          if (unlinked.length)  parts.push(`${unlinked.length} unlinked`);
          if (deactivated.length) parts.push(`${deactivated.length} deactivated`);
          if (errors.length)    parts.push(`${errors.length} failed`);

          const message = parts.length
            ? parts.join(", ")
            : "No changes made.";

          // ─── Decide toast colour (status) ───────────────────────────────────
          let toastStatus: "success" | "warning" | "error";
          if (errors.length) {
            toastStatus = "error";
          } else if (deleted.length || unlinked.length || deactivated.length) {
            toastStatus = "success";
          } else {
            toastStatus = "warning";   // nothing happened, but not an error
          }

          // ─── Show toast ────────────────────────────────────────────────────
          toast({
            title: "Delete questions",
            description: message,
            status: toastStatus,
            duration: 4000,
            isClosable: true,
          });
        } catch (err: any) {
          // Network / auth / unexpected error
          toast({
            title: "Delete failed",
            description: err.response?.data?.detail ?? err.message,
            status: "error",
            duration: 4000,
            isClosable: true,
          });
        }
      }

      // Refresh data
      await fetchQuestionSet();
      await fetchQuestions();
      setOperationMode("none");
      setSelectedForDelete([]);
      setSelectedQSetId(null);
      setSelectedQuestionId(null);
      setSelectedSequence(null);
    } catch (error) {
      toast({
        title: `Failed to ${operationMode} questions`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleCancelOperation = () => {
    setOperationMode("none");
    setSelectedForDelete([]);
    setSelectedQSetId(null);
    setSelectedQuestionId(null);
    setSelectedSequence(null);
  };

  // Handler for selecting questions for deletion
  const toggleSelectForDelete = (id: number) => {
    setSelectedForDelete((prev) =>
      prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]
    );
  };

  // Handler for adding an existing question from another set
  const handleAddQuestionToSet = async () => {
    if (!selectedSequence || !selectedQuestionId) return;
    try {
      const apiUrl = process.env.REACT_APP_B2C_API_URL;
      const token = localStorage.getItem("b2c_token");
      await axios.post(`${apiUrl}/qset/add-existing-question/${question_set_id}`, {
        question_id: selectedQuestionId,
        sequence_number: selectedSequence,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast({
        title: "Question added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      await fetchQuestions();
      setOperationMode("none");
    } catch (error) {
      toast({
        title: "Failed to add question",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };



  useEffect(() => {
    if (Number(question_set_id) > 0) {
      fetchQuestionSet();
      fetchQuestions();
    }
  }, [question_set_id]);


  // Compute available sequence numbers when questions change
  useEffect(() => {
    computeAvailableSequences();
  }, [questions]);

  // For "Add Existing" mode, fetch all question sets when the mode is enabled
  useEffect(() => {
    if (operationMode === "add") {
      fetchAllQuestionSets();
    }
  }, [operationMode]);


const listBg = useColorModeValue("white", "gray.700");
const selectedBg = useColorModeValue("blue.100", "blue.900");
const hoverBg = useColorModeValue("blue.50", "blue.800");

function replaceReferencesWithLabels(text: string, choiceIdToLabel: Record<number, string>): string {
  return text.replace(/@\{(\d+)\}/g, (_, id) => {
    const choiceId = parseInt(id);
    return choiceIdToLabel[choiceId] || `@{${id}}`; // fallback
  });
}


  // Render component
  return (
     <Box as="main" mx="auto" p={10} >
      <Box bg={useColorModeValue("gray.100","gray.700")} p={4} borderRadius="sm" mb={2}>
        <Flex direction={["column","row"]} justify="space-between" align={["flex-start","center"]} wrap="wrap">
          <Heading size="lg">{questionSet?.title}</Heading>
          <HStack spacing={2} mt={[2,0]}>
     
              <Badge colorScheme="purple">{questionSet?.category_name}</Badge>
         
              <Badge colorScheme="blue">{questionSet?.subject}</Badge>
           
              <Badge colorScheme="green">{questionSet?.grade_level}</Badge>
          
              <Badge colorScheme="orange">{questionSet?.target_exam}</Badge>
      
          </HStack>
        </Flex>

        <Flex mt={3} wrap="wrap" fontSize="sm" color={useColorModeValue("gray.600","gray.400")}>
          <Text mr={4}><strong>Description:</strong> {questionSet?.description || "—"}</Text>
          <Text mr={4}><strong>Default Max Score:</strong> {questionSet?.default_max_score}</Text>
          <Text mr={4}><strong>Duration:</strong> {questionSet?.duration} min</Text>
          <Text mr={4}><strong>Created:</strong> {new Date(questionSet?.created_at!).toLocaleDateString()}</Text>
          <Text><strong>Updated:</strong> {new Date(questionSet?.updated_at!).toLocaleDateString()}</Text>
        </Flex>
      </Box>
      {/* Top Bar */}

      <B2CQuestionSetAuditLog questionSetId={Number(question_set_id)} />

      <Divider orientation='horizontal'/>
      {/* Operations / Mode Selector Panel */}
      <Box mb={2} mt={2}>
        <HStack spacing={3}>
          <Button
           size={"sm"}
            colorScheme="teal"
            borderRadius="sm"
            onClick={() => {
              setOperationMode("resequence");
            }}
            isDisabled={operationMode === "delete" || operationMode === "add" || operationMode === "upload"}
          >
            Resequence Questions
          </Button>
          <Button
            size={"sm"}
            colorScheme="red"
            borderRadius="sm"
            onClick={() => {
              setOperationMode("delete");
            }}
            isDisabled={operationMode === "resequence" || operationMode === "add" || operationMode === "upload"}
          >
            Delete Questions
          </Button>
          <Button
            size={"sm"}
            colorScheme="cyan"
            borderRadius="sm"
            onClick={() => {
              setOperationMode("add");
              // Reset selection for "Add Existing" mode:
              setSelectedQSetId(null);
              setSelectedQuestionId(null);
              setSelectedSequence(null);
            }}
            isDisabled={operationMode === "resequence" || operationMode === "delete" || operationMode === "upload"}
          >
            Add Existing Question
          </Button>
          <Button
            size={"sm"}
            colorScheme="blue"
            borderRadius="sm"
            onClick={() => {
              setOperationMode("upload");
              // Clear file selection and any previous summary
              setFile(null);
              setUploadSummary(null);
            }}
            isDisabled={operationMode === "resequence" || operationMode === "delete" || operationMode === "add"}
          >
            Bulk Upload
          </Button>
          {(operationMode !== "none") && (
            <Button size={"sm"} variant="outline" colorScheme="red" onClick={handleCancelOperation}>
              Cancel
            </Button>
          )}
        </HStack>
      </Box>

      {/* Main Layout: Left Pane and Right Pane B2C Review Qset */}
      
    

      <Flex gap={6}>
        {/* Left Pane - Question List */}
        <Box
          w="30%"
          bg={useColorModeValue("gray.100","gray.700")}
          p={4}
          borderRadius="md"
          overflowY="auto"
          maxHeight="600px"
          opacity={(operationMode === "add" || operationMode === "upload") ? 0.4 : 1}
          pointerEvents={(operationMode === "add" || operationMode === "upload") ? "none" : "auto"}
        >
          <Heading size="sm" mb={4}>Questions</Heading>
          {questions.map((q, index) => (
            <Box
              key={q.question_id}
              p={2}
              borderRadius="md"
              bg={index === currentIndex ? selectedBg : listBg}
              _hover={{ bg: hoverBg, cursor: "pointer" }}
              mb={2}
              onClick={() => handleQuestionClick(index)}
            >
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  {operationMode === "delete" && (
                    <Checkbox
                      isChecked={selectedForDelete.includes(q.question_id)}
                      onChange={() => toggleSelectForDelete(q.question_id)}
                      onClick={e => e.stopPropagation()}
                    />
                  )}
                  <Text fontSize="sm" fontWeight="bold">
                    {q.sequence_number}. {q.question_text?.substring(0, 60)}…
                  </Text>
                </HStack>

                <HStack spacing={1}>
                  {q.is_immutable && <Badge colorScheme="red">Immutable</Badge>}
                  {q.is_shared &&   <Badge colorScheme="purple">Shared</Badge>}
                  {operationMode === "resequence" && (
                    <>
                      <IconButton
                        size="xs"
                        icon={<ChevronUpIcon />}
                        aria-label="Move Up"
                        onClick={e => { e.stopPropagation(); handleMoveUp(index); }}
                      />
                      <IconButton
                        size="xs"
                        icon={<ChevronDownIcon />}
                        aria-label="Move Down"
                        onClick={e => { e.stopPropagation(); handleMoveDown(index); }}
                      />
                    </>
                  )}
                </HStack>
              </Flex>
            </Box>
          ))}

          {(operationMode === "resequence" || operationMode === "delete") && (
            <HStack mt={4} spacing={3}>
              <Button
                size="sm"
                colorScheme="blue"
                onClick={handleCommit}
                isDisabled={operationMode === "delete" && selectedForDelete.length === 0}
              >
                Commit
              </Button>
              <Button size="sm" onClick={handleCancelOperation}>
                Cancel
              </Button>
            </HStack>
          )}
        </Box>

        {/* Right Pane - Detail / Operation Form */}
        <Box w="70%" p={4} border="1px solid" borderColor="gray.200" borderRadius="md">
          {operationMode === "add" ? (
            // Add Existing Question Form
            <VStack align="start" spacing={4}>
              <Heading size="sm" mb={2}>Add Existing Question</Heading>

              {/* Sequence Number Drop-down */}
              <Box>
                {/* <Text fontSize="sm">Sequence Number</Text> */}
                <Select
                  size={"sm"}
                  placeholder="Select Sequence"
                  value={selectedSequence ?? ""}
                  onChange={(e) => setSelectedSequence(Number(e.target.value))}
                >
                  {availableSequences.map(seq => (
                    <option key={seq} value={seq}>{seq}</option>
                  ))}
                </Select>
              </Box>

              {/* Question Set Selection */}
              <Box>
                {/* <Text fontSize="sm">Source Question Set</Text> */}
                <Select
                  size={"sm"}
                  placeholder="Select a Question Set"
                  value={selectedQSetId ?? ""}
                  onChange={(e) => {
                    const setId = Number(e.target.value);
                    setSelectedQSetId(setId);
                    fetchQuestionsFromSet(setId);
                  }}
                >
                  {allQuestionSets.map(qs => (
                    <option key={qs.question_set_id} value={qs.question_set_id}>
                      {qs.title}
                    </option>
                  ))}
                </Select>
              </Box>

              {/* Question Selection */}
              <Box>
                {/* <Text fontSize="sm">Select Question</Text> */}
                <Select
                  size={"sm"}
                  placeholder="Select a Question"
                  value={selectedQuestionId ?? ""}
                  onChange={(e) => setSelectedQuestionId(Number(e.target.value))}
                >
                  {availableQuestions.map(q => (
                    <option key={q.question_id} value={q.question_id}>
                      {q.question_text?.substring(0, 60)}...
                    </option>
                  ))}
                </Select>
              </Box>

              <HStack pt={4}>
                <Button isDisabled={!selectedQuestionId} size={"sm"} colorScheme="green" onClick={handleAddQuestionToSet}>
                  Add
                </Button>
                <Button size={"sm"} onClick={handleCancelOperation}>Cancel</Button>
              </HStack>
            </VStack>
          ) : operationMode === "upload" ? (
            // Bulk Upload Form (for ZIP)
            <Box>
              <Heading size="sm" mb={6}>Bulk Upload Questions (ZIP)</Heading>
              <VStack spacing={4} align="stretch" mb={4}>
                <Input type="file" accept=".zip" onChange={handleFileChange} size={"sm"} maxWidth="800px" />
              </VStack>
              <HStack mt={6} spacing={3}>
                <Button colorScheme="blue" size="sm" onClick={handleUpload}
                isLoading={isUploading}
                loadingText="Uploading"
                spinnerPlacement="start"
                >
                  Upload
                </Button>
                <Button size="sm" onClick={handleCancelOperation}>
                  Cancel
                </Button>

              </HStack>
              {isUploading && (
                <Text fontSize="sm" color="gray.500">
                  Processing ZIP file — this may take a few seconds…
                </Text>
              )}
              {uploadSummary && (
                <Alert
                  status="info"
                  variant="subtle"
                  mt={4}
                  flexDirection="column"
                  alignItems="flex-start"
                  position="relative"
                  w="100%"
                >
                  <AlertTitle mb={1}>Upload Summary</AlertTitle>
                  <AlertDescription w="100%">
                    <VStack align="start" spacing={3} w="100%">

                      {/* Added */}
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">
                          ✅ Total Questions Added: {uploadSummary.added.length}
                        </Text>
                        {uploadSummary.added.length > 0 && (
                          <Text fontSize="sm" pl={4} whiteSpace="pre-wrap">
                            Added Questions: [{uploadSummary.added.join(", ")}]
                          </Text>
                        )}
                      </Box>

                      {/* Updated */}
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">
                          🔁 Total Questions Updated: {uploadSummary.updated.length}
                        </Text>
                        {uploadSummary.updated.length > 0 && (
                          <Text fontSize="sm" pl={4} whiteSpace="pre-wrap">
                            Updated Questions: [{uploadSummary.updated.join(", ")}]
                          </Text>
                        )}
                      </Box>

                      {/* NEW — Overridden */}
                      <Box>
                        <Text fontSize="sm" fontWeight="bold" color="orange.600">
                          🛡️ Total Questions Force-Overridden: {uploadSummary.overridden.length}
                        </Text>
                        {uploadSummary.overridden.length > 0 && (
                          <Text fontSize="sm" pl={4} whiteSpace="pre-wrap" color="orange.600">
                            Overridden Questions (Seq #): [{uploadSummary.overridden.join(", ")}]
                          </Text>
                        )}
                      </Box>

                      {/* Skipped */}
                      <Box>
                        <Text fontSize="sm" fontWeight="bold">
                          ⛔ Total Skipped (Immutable): {uploadSummary.skipped_due_to_immutability.length}
                        </Text>
                        {uploadSummary.skipped_due_to_immutability.length > 0 && (
                          <Text fontSize="sm" pl={4} whiteSpace="pre-wrap">
                            Skipped Questions: [
                            {uploadSummary.skipped_due_to_immutability.join(", ")}]
                          </Text>
                        )}
                      </Box>

                      {/* Errors */}
                      <Box w="100%">
                        <Text fontSize="sm" fontWeight="bold" color="red.600">
                          ❌ Total Errors: {uploadSummary.errors.length}
                        </Text>
                        {uploadSummary.errors.length > 0 && (
                          <VStack align="start" pl={4} spacing={1}>
                            {uploadSummary.errors.map((err, idx) => (
                              <Text
                                key={idx}
                                fontSize="sm"
                                color="red.600"
                                whiteSpace="pre-wrap"
                              >
                                Error {idx + 1}: {err}
                              </Text>
                            ))}
                          </VStack>
                        )}
                      </Box>

                    </VStack>
                  </AlertDescription>

                  <CloseButton
                    position="absolute"
                    right="8px"
                    top="8px"
                    onClick={() => setUploadSummary(null)}
                  />
                </Alert>
              )}

            </Box>
            ) : currentQuestion ? (
              // View Mode for a selected question
              <>
                {hasValidContext(currentQuestion?.question_context) ? (
                  <Flex gap={4} align="stretch" mb={4}>
                    <Box w="45%" p={3} border="1px solid" borderColor="blue.200"
                      borderRadius="md" bg={contextPaneBg}
                      overflowY="auto" maxH="500px">
                      <Text fontSize="xs" fontWeight="bold" color="blue.500" mb={2}>CONTEXT</Text>
                      <RenderQuestionContext context={currentQuestion.question_context} />
                    </Box>
                    <Box w="55%">
                      <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-line" mr={4}>
                        Q{currentQuestion.sequence_number}:
                      </Text>
                      <RenderTextWithImages text={currentQuestion.question_text ?? ""} fontSize="sm" fontWeight="semibold" />
                      <HStack spacing={2} mt={1}>
                        {currentQuestion.is_immutable && <Badge size="xs" colorScheme="red">Immutable</Badge>}
                        {currentQuestion.is_shared    && <Badge size="xs" colorScheme="purple">Shared</Badge>}
                      </HStack>
                    </Box>
                  </Flex>
                ) : (
                  <Flex align="center" mb={2}>
                    <Box mr={4}>
                      <Text fontSize="sm" fontWeight="semibold" whiteSpace="pre-line">
                        Q{currentQuestion.sequence_number}:
                      </Text>
                      <RenderTextWithImages text={currentQuestion.question_text ?? ""} fontSize="sm" fontWeight="semibold" />
                    </Box>
                    <HStack spacing={2}>
                      {currentQuestion.is_immutable && <Badge size="xs" colorScheme="red">Immutable</Badge>}
                      {currentQuestion.is_shared    && <Badge size="xs" colorScheme="purple">Shared</Badge>}
                    </HStack>
                  </Flex>
                )}

                <Divider my={2} />

                {/* ── Choices + Correct Answer rendering, branched by question type ── */}

                {currentQuestion.question_type === "CLOZE_DROPDOWN" ? (
                  <>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Dropdown Groups:</Text>
                    <VStack align="start" spacing={3}>
                      {((currentQuestion.choices ?? []) as any[]).map((group, gIdx) => (
                        <Box key={gIdx} p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                          <Text fontWeight="semibold" fontSize="sm" mb={1}>
                            Dropdown D{group.dropdown_id}
                          </Text>
                          <VStack align="start" spacing={1} pl={3}>
                            {(group.options ?? []).map((opt: any) => (
                              <Box key={opt.id}>
                                <Text fontSize="sm">• {opt.text}</Text>
                                {opt.image && (
                                  <Image src={opt.image} alt="Option" maxW="100%" maxH="80px" objectFit="contain" mt={1} borderRadius="md" />
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </Box>
                      ))}
                    </VStack>

                    <Box mt={4} p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                      <Text fontSize="sm" fontWeight="semibold" mb={1}>Correct Answer:</Text>
                      {Object.entries((currentQuestion.correct_answer ?? {}) as Record<string, number[]>).map(([key, val]) => (
                        <Text fontSize="sm" key={key} ml={4}>
                          • D{key.replace(/^D/, "")}: option id(s) {Array.isArray(val) ? val.join(", ") : String(val)}
                        </Text>
                      ))}
                      <Text fontSize="sm" fontWeight="semibold" mt={2}>
                        Explanation: {currentQuestion.explanation}
                      </Text>
                    </Box>
                  </>

                ) : currentQuestion.question_type === "CLOZE_GAP_FILL" ? (
                  <>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Option Pool:</Text>
                    <VStack align="start" spacing={2}>
                      {(((currentQuestion.choices as any)?.option_pool ?? []) as any[]).map((opt: any) => (
                        <Box key={opt.id} p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                          <Text fontSize="sm">#{opt.id}: {opt.text}</Text>
                          {opt.image && (
                            <Image src={opt.image} alt="Option" maxW="100%" maxH="80px" objectFit="contain" mt={1} borderRadius="md" />
                          )}
                        </Box>
                      ))}
                    </VStack>

                    <Box mt={4} p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                      <Text fontSize="sm" fontWeight="semibold" mb={1}>Correct Answer (Gap → Option ID):</Text>
                      {Object.entries((currentQuestion.correct_answer ?? {}) as Record<string, number>).map(([key, val]) => (
                        <Text fontSize="sm" key={key} ml={4}>
                          • G{key.replace(/^G/, "")}: option id {val}
                        </Text>
                      ))}
                      <Text fontSize="sm" fontWeight="semibold" mt={2}>
                        Explanation: {currentQuestion.explanation}
                      </Text>
                    </Box>
                  </>

                ) : (
                  <>
                    <Text fontSize="sm" fontWeight="bold" mb={2}>Choices:</Text>
                    <VStack align="start" spacing={3}>
                      {(currentQuestion.choices ?? []).map((choice, idx) => {
                        const label = String.fromCharCode(65 + idx);
                        const choiceIdToLabel: Record<number, string> = {};
                        (currentQuestion.choices ?? []).forEach((c, i) => {
                          choiceIdToLabel[c.id] = String.fromCharCode(65 + i);
                        });

                        return (
                          <Box key={idx} p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                            <Text fontWeight="semibold" fontSize="sm">Label: {label}</Text>
                            <Text fontSize="sm">
                              Text: {replaceReferencesWithLabels(choice.text ?? "", choiceIdToLabel)}
                            </Text>
                            {choice.image && (
                              <Image src={choice.image} alt="Choice" width="auto" maxW="100%" maxH="100px"
                                objectFit="contain" mt={2} borderRadius="md" />
                            )}
                          </Box>
                        );
                      })}
                    </VStack>

                    <VStack align="start" spacing={3} mt={4}>
                      <Box p={2} border="1px solid" borderColor="gray.300" borderRadius="md" w="100%">
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>Correct Answer:</Text>
                        {currentQuestion.question_type === "SHORT_ANSWER" ? (
                          (currentQuestion.correct_answer as string[]).map((ans, idx) => (
                            <Text fontSize="sm" key={idx} ml={4}>• {ans}</Text>
                          ))
                        ) : (
                          (() => {
                            const choiceIdToLabel: Record<number, string> = {};
                            (currentQuestion.choices ?? []).forEach((choice, index) => {
                              choiceIdToLabel[choice.id] = String.fromCharCode(65 + index);
                            });

                            return (currentQuestion.correct_answer as number[]).map((index, idx) => {
                              const correctedIndex = Number(index) - 1;
                              const choice = currentQuestion.choices?.[correctedIndex];
                              return (
                                <Box key={idx} ml={4} mb={2}>
                                  <Text fontSize="sm">
                                    • {choice ? choiceIdToLabel[choice.id] : "?"}:{" "}
                                    {choice ? replaceReferencesWithLabels(choice.text ?? "", choiceIdToLabel) : "Unknown choice"}
                                  </Text>
                                  {choice?.image && (
                                    <Image src={choice.image} alt="Correct choice" width="auto" maxW="100%" maxH="100px"
                                      objectFit="contain" mt={1} borderRadius="md" />
                                  )}
                                </Box>
                              );
                            });
                          })()
                        )}
                        <Text fontSize="sm" fontWeight="semibold" mb={1}>
                          Explanation : {currentQuestion.explanation}
                        </Text>
                      </Box>
                    </VStack>
                  </>
                )}

                <Flex justify="space-between" mt={6}>
                  <Button size="sm" onClick={() => setCurrentIndex(i => Math.max(i - 1, 0))} isDisabled={currentIndex === 0}>
                    Previous
                  </Button>
                  <Button size="sm" onClick={() => setCurrentIndex(i => Math.min(i + 1, questions.length - 1))} isDisabled={currentIndex === questions.length - 1}>
                    Next
                  </Button>
                </Flex>
              </>
            ) : (
              <Text>No question selected.</Text>
            )}
        </Box>
      </Flex>


    </Box>
  );
};

export default B2CReviewQset;
